import React from 'react';
import styled, { keyframes } from 'styled-components';
import { MediaQuery, Palette } from 'core/config';
import { SectionInner } from '../styles';

export type ClientLogoItem =
    | { type: 'image'; src: string; alt: string }
    | { type: 'text'; label: string };

export type ClientLogoMarqueeProps = {
    title: string;
    items: ClientLogoItem[];
    marqueeEnabled?: boolean;
    repeatItems?: boolean;
    /** Aligns title with parent padding — no nested SectionInner inset. */
    embedded?: boolean;
};

export const ClientLogoMarquee: React.FunctionComponent<ClientLogoMarqueeProps> = ({
    title,
    items,
    marqueeEnabled = true,
    repeatItems = true,
    embedded = false,
}) =>
{
    const trackItems = repeatItems ? [ ...items, ...items ] : items;

    return (
        <Section $embedded={embedded} aria-label={title}>
            {embedded ? (
                <TitleBar>
                    <Label>{title}</Label>
                </TitleBar>
            ) : (
                <SectionInner>
                    <Label>{title}</Label>
                </SectionInner>
            )}

            <Carousel $embedded={embedded} aria-hidden="true">
                <Track $animate={marqueeEnabled} $embedded={embedded}>
                    {trackItems.map((item, idx) => (
                        <LogoCell key={`${item.type}-${'label' in item ? item.label : item.alt}-${idx}`}>
                            {item.type === 'image' ? (
                                <LogoImage src={item.src} alt={item.alt} loading="lazy" />
                            ) : (
                                <LogoText>{item.label}</LogoText>
                            )}
                        </LogoCell>
                    ))}
                </Track>
            </Carousel>
        </Section>
    );
};

const marquee = keyframes`
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
`;

const Section = styled.section<{ $embedded: boolean }>`
    background: ${Palette.WHITE};
    color: ${Palette.BLACK};
    padding: ${({ $embedded }) => ($embedded ? '0' : '28px 0 40px')};
    border-bottom: ${({ $embedded }) => ($embedded ? 'none' : '1px solid rgba(0, 0, 0, 0.06)')};
`;

const TitleBar = styled.div`
    width: 100%;
`;

const Label = styled.p`
    margin: 0 0 18px;
    font-family: 'Poppins-Regular', sans-serif;
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    text-align: left;
    color: rgba(0, 0, 0, 0.55);
`;

const Carousel = styled.div<{ $embedded: boolean }>`
    position: relative;
    overflow: hidden;
    width: 100%;
    mask-image: ${({ $embedded }) =>
        $embedded
            ? `linear-gradient(
                90deg,
                black 0%,
                black 92%,
                transparent 100%
            )`
            : `linear-gradient(
                90deg,
                transparent 0%,
                black 8%,
                black 92%,
                transparent 100%
            )`};
`;

const Track = styled.div<{ $animate: boolean; $embedded: boolean }>`
    display: flex;
    align-items: center;
    gap: 18px;
    width: max-content;
    padding: ${({ $embedded }) => ($embedded ? '0' : '0 24px')};
    animation: ${({ $animate }) => ($animate ? marquee : 'none')} 28s linear infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
        overflow-x: auto;
        width: 100%;
    }

    @media ${MediaQuery.tablet} {
        gap: 22px;
        padding: ${({ $embedded }) => ($embedded ? '0' : '0 48px')};
        animation-duration: 34s;
    }
`;

const LogoCell = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 46px;
    min-width: 150px;
    padding: 0 10px;
    border-radius: 0;
    border: none;
    background: transparent;
    box-shadow: none;
`;

const LogoImage = styled.img`
    max-height: 28px;
    max-width: 150px;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: grayscale(100%);
    opacity: 0.82;
    transition: opacity 0.2s ease, filter 0.2s ease;

    ${LogoCell}:hover & {
        filter: grayscale(0%);
        opacity: 1;
    }
`;

const LogoText = styled.span`
    font-family: 'Poppins-Medium', sans-serif;
    font-size: 14px;
    letter-spacing: -0.01em;
    color: rgba(0, 0, 0, 0.82);
    white-space: nowrap;
`;

